/**
 * PD-17378 — where a payment came from, in words a customer uses.
 *
 * `m_payment_transaction.m_payment_origin` stores the code of whatever wrote
 * the row: `ecommerce` for the storefront, `PaymentLink` for an emailed
 * payment link, `CRM` for staff, `StopCompletion` for a driver, `ERP` for
 * an imported payment, and a handful of system codes (`AdyenWebhook`,
 * `OrderCancellation`, `SettlementReconciliation`, `API`). Reports group
 * those codes into the few origins a customer asks about ("how much came in
 * through the storefront?"), and every surface that filters or shows an
 * origin uses the groups below, so Cash Receipts, Storefront Engagement and
 * the Transactions list cannot disagree about which payments are online.
 *
 * Codes are matched case-insensitively and trimmed: SM1 wrote `Ecommerce`
 * where Workd writes `ecommerce` (South Pointe has 585 such rows), and a
 * missing, empty or unknown code is Other, never dropped, so the groups
 * always add up to the whole report.
 *
 * Callers group a payment CHAIN by its origin transaction (the row that
 * started it), not by each row's own code. A refund is written with the
 * origin of whoever issued it (CRM, API, AdyenWebhook), and a storefront
 * card hold captured later by the payments job carries `Automated`; grouped
 * by the chain's origin, both stay with the storefront payment they belong to.
 */

/** The customer-facing origin groups. */
export type PaymentOriginGroup =
	| 'storefront'
	| 'paymentLink'
	| 'staff'
	| 'delivery'
	| 'autopay'
	| 'erp'
	| 'other';

export interface PaymentOriginGroupDefinitionModel {
	key: PaymentOriginGroup;
	/** Untranslated label; the UI passes it through t(). */
	label: string;
	/** The stored origin codes in this group, as written (matched case-insensitively). */
	codes: ReadonlyArray<string>;
}

/**
 * Every group in display order. Other has no codes of its own: it is
 * whatever no other group claims, including no code at all.
 */
export const PAYMENT_ORIGIN_GROUPS: ReadonlyArray<PaymentOriginGroupDefinitionModel> =
	[
		{ key: 'storefront', label: 'Storefront', codes: ['ecommerce'] },
		{ key: 'paymentLink', label: 'Payment link', codes: ['PaymentLink'] },
		{ key: 'staff', label: 'Staff', codes: ['CRM', 'VoiceAgent'] },
		{
			key: 'delivery',
			label: 'Delivery',
			codes: ['StopCompletion', 'StopCollection', 'at-delivery'],
		},
		{
			key: 'autopay',
			label: 'Autopay / scheduled',
			codes: ['Automated', 'Scheduled', 'CycleBilling'],
		},
		{ key: 'erp', label: 'ERP', codes: ['ERP'] },
		{ key: 'other', label: 'Other', codes: [] },
	];

export const PAYMENT_ORIGIN_GROUP_KEYS: ReadonlyArray<PaymentOriginGroup> =
	PAYMENT_ORIGIN_GROUPS.map((group) => group.key);

/** Payments the customer made online: on the storefront or through a payment link. */
export const ONLINE_PAYMENT_ORIGIN_GROUPS: ReadonlyArray<PaymentOriginGroup> = [
	'storefront',
	'paymentLink',
];

const normalizeCode = (code: string): string => code.trim().toLowerCase();

const GROUP_BY_CODE: ReadonlyMap<string, PaymentOriginGroup> = new Map(
	PAYMENT_ORIGIN_GROUPS.flatMap((group) =>
		group.codes.map((code): [string, PaymentOriginGroup] => [
			normalizeCode(code),
			group.key,
		]),
	),
);

const LABEL_BY_GROUP: ReadonlyMap<PaymentOriginGroup, string> = new Map(
	PAYMENT_ORIGIN_GROUPS.map((group): [PaymentOriginGroup, string] => [
		group.key,
		group.label,
	]),
);

export function isPaymentOriginGroup(
	value: unknown,
): value is PaymentOriginGroup {
	return (
		typeof value === 'string' &&
		(PAYMENT_ORIGIN_GROUP_KEYS as ReadonlyArray<string>).includes(value)
	);
}

/** The group of a stored origin code. Missing, empty and unknown codes are Other. */
export function toPaymentOriginGroup(
	code: string | null | undefined,
): PaymentOriginGroup {
	if (code == null) {
		return 'other';
	}

	return GROUP_BY_CODE.get(normalizeCode(code)) ?? 'other';
}

/** The untranslated label of a group; anything that is not a group reads as Other. */
export function paymentOriginGroupLabel(group: unknown): string {
	return (
		(isPaymentOriginGroup(group) ? LABEL_BY_GROUP.get(group) : undefined) ??
		'Other'
	);
}

/**
 * The groups a filter asked for. Each value is a group key, or a stored
 * origin code read as its group: the Transactions list's Origin filter used
 * to offer raw codes (CRM, Ecommerce, ERP, External, Automated), and saved
 * searches still hold them, so a saved "Ecommerce" keeps meaning the
 * storefront. Non-string and blank values are ignored; the result has no
 * duplicates and follows the display order.
 */
export function resolvePaymentOriginFilterGroups(
	values: unknown,
): Array<PaymentOriginGroup> {
	const list = Array.isArray(values) ? values : [values];
	const picked = new Set<PaymentOriginGroup>();

	for (const value of list) {
		if (typeof value !== 'string' || value.trim() === '') {
			continue;
		}

		picked.add(isPaymentOriginGroup(value) ? value : toPaymentOriginGroup(value));
	}

	return PAYMENT_ORIGIN_GROUP_KEYS.filter((key) => picked.has(key));
}

/** A code-authored string as a SQL literal. */
const sqlLiteral = (value: string): string => `'${value.replace(/'/g, "''")}'`;

const buildGroupCaseSql = (
	originCodeSql: string,
	output: (group: PaymentOriginGroupDefinitionModel) => string,
): string => {
	const normalized = `LOWER(TRIM(${originCodeSql}))`;
	const branches = PAYMENT_ORIGIN_GROUPS.filter(
		(group) => group.codes.length > 0,
	).map(
		(group) =>
			`WHEN ${normalized} IN (${group.codes
				.map((code) => sqlLiteral(normalizeCode(code)))
				.join(', ')}) THEN ${sqlLiteral(output(group))}`,
	);

	// NULL fails every IN, so a missing code lands on ELSE with the unknown ones.
	return `(CASE ${branches.join(' ')} ELSE ${sqlLiteral(
		output({ key: 'other', label: 'Other', codes: [] }),
	)} END)`;
};

/**
 * SQL for the group key of an origin code expression, e.g.
 * `buildPaymentOriginGroupSql('pt.m_payment_origin')`. The expression is
 * interpolated, so it must be code-authored, never user input. The codes
 * are lower-cased on both sides so the match does not rely on the column's
 * collation.
 */
export function buildPaymentOriginGroupSql(originCodeSql: string): string {
	return buildGroupCaseSql(originCodeSql, (group) => group.key);
}

/** SQL for the untranslated group label of an origin code expression, for CSV exports. */
export function buildPaymentOriginLabelSql(originCodeSql: string): string {
	return buildGroupCaseSql(originCodeSql, (group) => group.label);
}
